/**
 * ClubPickerDrawer — "pick and go" drawer listing the user's clubs.
 *
 * Clicking a club closes the drawer and navigates to /clubs/:clubId.
 * An inline "Create Club" form at the bottom allows creating a new club,
 * after which the drawer closes and navigates to the new club page.
 */
import { useState, useEffect } from 'react';
import {
  Box,
  Drawer,
  Typography,
  IconButton,
  CircularProgress,
  Alert,
  Divider,
  Button,
  TextField,
  Collapse,
  Badge,
  Tooltip,
  Chip,
  ToggleButton,
  ToggleButtonGroup,
  useTheme,
} from '@mui/material';
import {
  Close as CloseIcon,
  Group as GroupIcon,
  Add as AddIcon,
  ChevronRight as ChevronRightIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { tokens } from '../../theme';
import { getMyClubs, createClub } from '../../api/clubApi';

// Fallback club colors
const CLUB_COLORS = [
  '#4CAF50', '#2196F3', '#FF5722', '#9C27B0',
  '#FF9800', '#00BCD4', '#F44336', '#3F51B5',
];

const fmtDuration = (secs) => {
  if (!secs) return '';
  const h = Math.floor(secs / 3600);
  const m = Math.floor((secs % 3600) / 60);
  return h > 0 ? `${h}h ${String(m).padStart(2, '0')}m` : `${m}m`;
};

export default function ClubPickerDrawer({ open, onClose }) {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const navigate = useNavigate();

  const [clubs, setClubs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Create form state
  const [createOpen, setCreateOpen] = useState(false);
  const [newName, setNewName] = useState('');
  const [newDescription, setNewDescription] = useState('');
  const [newColor, setNewColor] = useState(CLUB_COLORS[0]);
  const [newSportType, setNewSportType] = useState('running');
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState('');

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    setError('');
    getMyClubs()
      .then(setClubs)
      .catch(() => setError('Failed to load clubs'))
      .finally(() => setLoading(false));
  }, [open]);

  const handleSelectClub = (club) => {
    onClose();
    navigate(`/clubs/${club.id}`);
  };

  const handleCreateClub = async () => {
    if (!newName.trim()) {
      setCreateError('Club name is required');
      return;
    }
    setCreating(true);
    setCreateError('');
    try {
      const club = await createClub({ name: newName.trim(), description: newDescription || undefined, color: newColor, sport_type: newSportType });
      onClose();
      navigate(`/clubs/${club.id}`);
    } catch (err) {
      setCreateError(err?.response?.data?.detail || 'Failed to create club');
    } finally {
      setCreating(false);
    }
  };

  const paperBg = theme.palette.mode === 'dark' ? colors.grey[800] : colors.grey[900];
  const cardBg = theme.palette.mode === 'dark' ? colors.grey[700] : colors.grey[800];

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          width: { xs: '100vw', sm: 400 },
          backgroundColor: paperBg,
          display: 'flex',
          flexDirection: 'column',
        },
      }}
    >
      {/* Header */}
      <Box
        display="flex"
        alignItems="center"
        justifyContent="space-between"
        p={2}
        sx={{ borderBottom: `1px solid ${colors.grey[700]}` }}
      >
        <Box display="flex" alignItems="center" gap={1}>
          <GroupIcon sx={{ color: colors.blueAccent[400] }} />
          <Typography variant="h6" fontWeight="bold" color={colors.grey[100]}>
            My Clubs
          </Typography>
        </Box>
        <IconButton onClick={onClose} size="small">
          <CloseIcon sx={{ color: colors.grey[300] }} />
        </IconButton>
      </Box>

      {/* Club list */}
      <Box flex={1} overflow="auto" p={1.5}>
        {loading && (
          <Box display="flex" justifyContent="center" py={4}>
            <CircularProgress size={32} />
          </Box>
        )}

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>
        )}

        {!loading && !error && clubs.length === 0 && (
          <Box py={4} textAlign="center">
            <GroupIcon sx={{ fontSize: 48, color: colors.grey[600], mb: 1 }} />
            <Typography color={colors.grey[400]} variant="body2">
              No clubs yet — create your first club below.
            </Typography>
          </Box>
        )}

        {!loading && clubs.map((club) => (
          <Box
            key={club.id}
            onClick={() => handleSelectClub(club)}
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1.5,
              p: 1.5,
              mb: 1,
              borderRadius: 1,
              backgroundColor: cardBg,
              cursor: 'pointer',
              border: `1px solid ${colors.grey[700]}`,
              '&:hover': {
                borderColor: colors.blueAccent[500],
                backgroundColor: theme.palette.mode === 'dark' ? colors.grey[600] : colors.grey[700],
              },
            }}
          >
            {/* Color dot */}
            <Box
              sx={{
                width: 12,
                height: 12,
                borderRadius: '50%',
                backgroundColor: club.color || colors.blueAccent[400],
                flexShrink: 0,
              }}
            />

            {/* Club info */}
            <Box flex={1} minWidth={0}>
              <Box display="flex" alignItems="center" gap={1}>
                <Typography
                  variant="body2"
                  fontWeight="bold"
                  color={colors.grey[100]}
                  noWrap
                >
                  {club.name}
                </Typography>
                {club.pending_count > 0 && (
                  <Badge
                    badgeContent={club.pending_count}
                    color="error"
                    sx={{ '& .MuiBadge-badge': { fontSize: '0.65rem', height: 16, minWidth: 16 } }}
                  >
                    <Box sx={{ width: 8 }} />
                  </Badge>
                )}
              </Box>
              <Box display="flex" alignItems="center" gap={0.75} flexWrap="wrap">
                <Typography variant="caption" color={colors.grey[400]}>
                  {club.member_count} member{club.member_count !== 1 ? 's' : ''} · {club.file_count} file{club.file_count !== 1 ? 's' : ''}
                </Typography>
                <Chip
                  label={club.sport_type === 'both' ? 'Tri' : club.sport_type === 'biking' ? 'Bike' : 'Run'}
                  size="small"
                  sx={{
                    height: 16,
                    fontSize: '0.6rem',
                    backgroundColor: club.sport_type === 'both' ? '#9c27b0' : club.sport_type === 'biking' ? '#1976d2' : '#388e3c',
                    color: '#fff',
                  }}
                />
              </Box>
            </Box>

            <ChevronRightIcon sx={{ color: colors.grey[500], flexShrink: 0 }} />
          </Box>
        ))}
      </Box>

      <Divider sx={{ borderColor: colors.grey[700] }} />

      {/* Create club section */}
      <Box p={1.5}>
        <Button
          fullWidth
          variant="text"
          startIcon={createOpen ? <ExpandLessIcon /> : <AddIcon />}
          onClick={() => { setCreateOpen(v => !v); setCreateError(''); }}
          sx={{
            justifyContent: 'flex-start',
            textTransform: 'none',
            color: colors.blueAccent[400],
            mb: createOpen ? 1.5 : 0,
          }}
        >
          Create Club
        </Button>

        <Collapse in={createOpen}>
          <Box display="flex" flexDirection="column" gap={1.5}>
            <TextField
              label="Club name"
              size="small"
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              fullWidth
              inputProps={{ maxLength: 100 }}
              sx={{ '& .MuiInputBase-root': { backgroundColor: cardBg } }}
            />
            <TextField
              label="Description (optional)"
              size="small"
              value={newDescription}
              onChange={(e) => setNewDescription(e.target.value)}
              fullWidth
              multiline
              rows={2}
              sx={{ '& .MuiInputBase-root': { backgroundColor: cardBg } }}
            />

            {/* Color picker */}
            <Box>
              <Typography variant="caption" color={colors.grey[400]} mb={0.5} display="block">
                Club color
              </Typography>
              <Box display="flex" gap={1} flexWrap="wrap">
                {CLUB_COLORS.map((c) => (
                  <Tooltip key={c} title={c}>
                    <Box
                      onClick={() => setNewColor(c)}
                      sx={{
                        width: 24,
                        height: 24,
                        borderRadius: '50%',
                        backgroundColor: c,
                        cursor: 'pointer',
                        border: newColor === c ? `3px solid ${colors.grey[100]}` : '2px solid transparent',
                        outline: newColor === c ? `1px solid ${c}` : 'none',
                      }}
                    />
                  </Tooltip>
                ))}
              </Box>
            </Box>

            {/* Sport type */}
            <Box>
              <Typography variant="caption" color={colors.grey[400]} mb={0.5} display="block">
                Sport type
              </Typography>
              <ToggleButtonGroup
                value={newSportType}
                exclusive
                onChange={(_, v) => { if (v) setNewSportType(v); }}
                size="small"
                sx={{ '& .MuiToggleButton-root': { textTransform: 'none', fontSize: '0.75rem', px: 1.5, py: 0.5 } }}
              >
                <ToggleButton value="running">Running</ToggleButton>
                <ToggleButton value="biking">Biking</ToggleButton>
                <ToggleButton value="both">Both (Tri)</ToggleButton>
              </ToggleButtonGroup>
            </Box>

            {createError && (
              <Alert severity="error" sx={{ py: 0.5 }}>{createError}</Alert>
            )}

            <Button
              variant="contained"
              onClick={handleCreateClub}
              disabled={creating}
              startIcon={creating ? <CircularProgress size={14} /> : <AddIcon />}
              sx={{ textTransform: 'none' }}
            >
              {creating ? 'Creating…' : 'Create Club'}
            </Button>
          </Box>
        </Collapse>
      </Box>
    </Drawer>
  );
}
